import { type HTMLAttributes, memo, useCallback, useMemo, useState } from 'react';
import { navigationHelper } from 'NavigationHelpers';
import { type LearnerProject } from 'LearnerProjects';
import { type ProjectProgress } from 'ContentProgress';
import {
    type ProjectGroupMembershipSansProjectGroup,
    projectGroupsApi,
    ProjectGroupMembershipStatus,
} from 'ProjectGroup';
import './locales/project_feedback-en.json';
import { Modal } from 'Modal';
import { Trans, useTranslation } from 'react-i18next';
import { Text } from 'Text';
import { Divider as HeroUIDivider } from '@heroui/react';
import { ClickableText } from 'ClickableText';
import Button from 'Button';
import clsx from 'clsx';
import { RenderMarkdown } from 'MarkdownComponents';
import { formattedUserFacingMonthDayYearLong } from 'DateHelpers';
import { useCurrentUser, useTargetBrandConfig } from 'FrontRoyalAngular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/pro-regular-svg-icons/faCloudArrowDown';
import { useErrorLogService } from 'ErrorLogging';
import { downloadProjectFeedbackPdf } from './downloadProjectFeedbackPdf';

const MAX_PROJECT_SCORE = 5;

const Divider = () => <HeroUIDivider className="-mx-7.5 my-7.5 w-auto" />;

const CodeOfConductLink = () => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'project_feedback' });
    const { loadFaqUrl } = navigationHelper();
    return (
        <ClickableText onPress={() => loadFaqUrl('/help/student-code-of-conduct', '_blank')}>
            {t('code_of_conduct')}
        </ClickableText>
    );
};

const ProjectGradingLink = () => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'project_feedback' });
    const { loadFaqUrl } = navigationHelper();
    return <ClickableText onPress={() => loadFaqUrl('/help/project-grading', '_blank')}>{t('graded')}</ClickableText>;
};

const ProjectGrade = ({
    progress,
    failed,
    passed,
    resubmissionRequired,
}: {
    progress: ProjectProgress;
    failed: boolean;
    passed: boolean;
    resubmissionRequired?: boolean;
}) => {
    const isResubmissionRequired = resubmissionRequired ?? progress.resubmissionRequired;
    const { t } = useTranslation('back_royal', { keyPrefix: 'project_feedback' });

    // While in theory explanation may be null, in practice it never will be. We won't be rendering this component if the project isn't passed or failed.
    let explanation: React.ReactNode | null = null;

    if (isResubmissionRequired) {
        explanation = (
            <Trans
                i18nKey="project_grade_explanation_resubmission_required"
                t={t}
                components={{
                    strong: <span className="font-bold" />,
                    resubmitByDate: progress.resubmissionDueDate ? (
                        <span className="font-regular" />
                    ) : (
                        <span style={{ display: 'none' }} />
                    ),
                }}
                values={{
                    dueDate: progress?.resubmissionDueDate
                        ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
                        : '',
                    maxScore: MAX_PROJECT_SCORE,
                }}
            />
        );
    } else if (failed) {
        explanation = (
            <Trans
                i18nKey="project_grade_explanation_failed"
                t={t}
                components={{
                    strong: <span className="font-bold" />,
                    resubmitByDate:
                        progress.allowResubmission && progress.resubmissionDueDate ? (
                            <span className="font-regular" />
                        ) : (
                            <span style={{ display: 'none' }} />
                        ),
                }}
                values={{
                    dueDate: progress?.resubmissionDueDate
                        ? formattedUserFacingMonthDayYearLong(progress.resubmissionDueDate * 1000)
                        : '',
                    score: progress.score,
                    maxScore: MAX_PROJECT_SCORE,
                }}
            />
        );
    } else if (passed) {
        explanation = (
            <Trans
                i18nKey="project_grade_explanation_passed"
                t={t}
                components={{
                    codeOfConductLink: <CodeOfConductLink />,
                    projectGradingLink: <ProjectGradingLink />,
                }}
                values={{
                    score: progress.score,
                    maxScore: MAX_PROJECT_SCORE,
                }}
            />
        );
    }

    const gradeDisplay = isResubmissionRequired
        ? t('resubmission_required_label')
        : `${progress.score} / ${MAX_PROJECT_SCORE}`;

    return (
        <div>
            <Text as="h2" color="black" className="mb-[10px]" weight="normal">
                {t('project_grade')}
                <span
                    className={clsx('ml-4 font-semibold', failed || isResubmissionRequired ? 'text-red' : 'text-black')}
                >
                    {gradeDisplay}
                </span>
            </Text>
            <Text className="text-small leading-[1.33] text-slate-grey">{explanation}</Text>
        </div>
    );
};

const FeedbackMarkdownParagraph = ({ children }: HTMLAttributes<HTMLElement>) => (
    <p className="mb-3 leading-[1.33] last:mb-0">{children}</p>
);

const FeedbackMarkdownH2 = ({ children }: HTMLAttributes<HTMLElement>) => (
    <h2 className="mb-4 text-md font-semibold text-black">{children}</h2>
);

const ProjectFeedback = ({ progress }: { progress: ProjectProgress }) => (
    // The auto-grader typically uses h2 headings in its markdown feedback, but sometimes it's h1, so we just force them both to
    // render as h2.
    <RenderMarkdown
        components={{ h1: FeedbackMarkdownH2, h2: FeedbackMarkdownH2, p: FeedbackMarkdownParagraph }}
        className="text-base text-black"
    >
        {progress.feedback}
    </RenderMarkdown>
);

const FooterButtons = ({
    closeModal,
    onDownloadPdf,
    isDownloading,
}: {
    closeModal: () => void;
    onDownloadPdf: () => void;
    isDownloading: boolean;
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'project_feedback' });
    return (
        <div className="my-7.5 flex items-center justify-center">
            <button
                type="button"
                className="mr-8 flex cursor-pointer items-center gap-1.5 text-md font-semibold text-black hover:opacity-60 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onDownloadPdf}
                disabled={isDownloading}
            >
                {t('download_pdf')}
                <FontAwesomeIcon icon={faCloudArrowDown} />
            </button>
            <Button color="black" height={12.5} className="pt-1" onPress={closeModal}>
                {t('return_to_dashboard')}
            </Button>
        </div>
    );
};

type ProjectFeedbackModalProps = {
    closeModal: () => void;
    isOpen: boolean;
    progress: ProjectProgress;
    project: LearnerProject;
    failed: boolean;
    passed: boolean;
    acceptedProjectGroupMembership?: ProjectGroupMembershipSansProjectGroup;
    resubmissionRequired?: boolean;
};

function ProjectFeedbackModalComponent(props: ProjectFeedbackModalProps) {
    const {
        closeModal,
        isOpen,
        progress,
        project,
        failed,
        passed,
        acceptedProjectGroupMembership,
        resubmissionRequired,
    } = props;
    const { t } = useTranslation('back_royal', { keyPrefix: 'project_feedback' });
    const currentUser = useCurrentUser();
    const ErrorLogService = useErrorLogService();
    const { branding: brand } = useTargetBrandConfig();
    const title = t('title');
    const [isDownloading, setIsDownloading] = useState(false);

    // Fetch group data if this is a group project with an accepted membership
    const { data: projectGroupData } = projectGroupsApi.useGetProjectGroupQuery(
        acceptedProjectGroupMembership?.projectGroupId ?? '',
        { skip: !acceptedProjectGroupMembership?.projectGroupId },
    );

    const groupMemberNames = useMemo(() => {
        const group = projectGroupData?.contents?.projectGroups?.[0];
        if (!group?.memberships) return undefined;
        return group.memberships
            .filter(m => m.status === ProjectGroupMembershipStatus.ACCEPTED)
            .map(m => m.user?.name)
            .filter((name): name is string => !!name);
    }, [projectGroupData]);

    const submitterName = groupMemberNames ? undefined : currentUser?.name;

    const onDownloadPdf = useCallback(async () => {
        setIsDownloading(true);
        try {
            await downloadProjectFeedbackPdf({
                project,
                progress,
                passed,
                failed,
                brand,
                t,
                groupMemberNames,
                submitterName,
            });
        } catch (error) {
            ErrorLogService.notify('Failed to generate project feedback PDF', error, {
                projectTitle: project.title,
                requirementIdentifier: project.requirementIdentifier,
            });
        } finally {
            setIsDownloading(false);
        }
    }, [project, progress, passed, failed, brand, t, groupMemberNames, submitterName, ErrorLogService]);

    if (!isOpen) return null;

    return (
        <Modal
            classNames={{
                header: 'pb-[10px]',
                body: 'select-text-important',
            }}
            dismissOnEscape
            dismissOnOverlayClick
            onClose={closeModal}
            scrollBehavior="outside"
            size="2xl"
            title={title}
        >
            <div className="mx-7.5">
                <Text as="h2" className="mx-7.5 mb-7.5 text-center" color="slate-grey" weight="normal" uppercase>
                    {project.title}
                </Text>
                <Divider />
                {groupMemberNames && groupMemberNames.length > 0 && (
                    <div className="mb-[10px]">
                        <Text as="h2" color="black" weight="normal">
                            {t('project_team')}
                            <span className="ml-4 font-semibold">{groupMemberNames.join(', ')}</span>
                        </Text>
                    </div>
                )}
                <ProjectGrade
                    progress={progress}
                    failed={failed}
                    passed={passed}
                    resubmissionRequired={resubmissionRequired}
                />
                <Divider />
                <ProjectFeedback progress={progress} />
                <FooterButtons closeModal={closeModal} onDownloadPdf={onDownloadPdf} isDownloading={isDownloading} />
            </div>
        </Modal>
    );
}
export const ProjectFeedbackModal = memo(ProjectFeedbackModalComponent) as typeof ProjectFeedbackModalComponent;
